import { FC, useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { EnvironmentContext } from 'config/environment'
import { TeamsChartActivateDocument } from 'generated/resources/graphql'
import { useLocalStorage } from 'usehooks-ts'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useTeamChartFeature } from 'modules/teams/hooks/useTeamChartFeature'

import './index.styles.css'
type Props = {
  companyId: string
}
const TeamChartActivationBanner: FC<Props> = ({ companyId }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { teamChartFeatureEnabled, teamChartFeature } = useTeamChartFeature(companyId)

  const [hideBanner, setHideBanner] = useLocalStorage(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideTeamChartActivationBanner,
    false
  )

  const [activateFeature, { loading }] = useMutation(TeamsChartActivateDocument, {
    variables: { companyId },
    extractorFn: (data) => data.teamsMutations?.activateFeatureTeamsTeamChart,
    update(cache, result) {
      const errors = result?.data?.teamsMutations?.activateFeatureTeamsTeamChart?.errors
      if (!errors?.length && teamChartFeature) {
        cache.modify({
          id: cache.identify(teamChartFeature),
          fields: {
            enabled() {
              return true
            },
          },
        })
      }
    },
  })

  const handleActivation = async () => {
    const result = await activateFeature({})
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      navigate(navigation.employees.teams.chart.fullPath())
    }
  }

  const { hideOrgChartBannerAndHint } = useContext(EnvironmentContext)

  if (hideBanner || teamChartFeatureEnabled || hideOrgChartBannerAndHint) {
    return null
  }

  return (
    <Box
      width="full"
      position="relative"
      boxShadow="s50"
      borderRadius={{ all: 'abs008' }}
      flexDirection="row"
      padding="s24"
      alignItems="center"
      justifyContent="spaceBetween"
      gradientBackground="purple200"
    >
      <Box gap="s16" flexDirection="row" alignItems="flexStart">
        <Box
          background="grey200"
          borderRadius={{ all: 'abs016' }}
          padding="s8"
          justifyContent="center"
          alignItems="center"
        >
          <Icon.Large icon="org-chart" color="grey800" />
        </Box>
        <Box>
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('teams.chart_activation_banner.title')}
          </Text.Normal>
          <Text.Normal>
            {i18n.t('teams.chart_activation_banner.description_question')}
            <br />
            {i18n.t('teams.chart_activation_banner.description_answer')}
          </Text.Normal>
        </Box>
      </Box>
      <Button onClick={handleActivation} loading={loading}>
        {i18n.t('teams.chart_activation_banner.activate')}
      </Button>
      <Box className={'TeamChartActivationBanner_removable'}>
        <Touchable aria-label={i18n.t('common.close')} onClick={() => setHideBanner(true)}>
          <Icon.Small icon="close" color="grey800" />
        </Touchable>
      </Box>
    </Box>
  )
}

export default TeamChartActivationBanner
